import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { CustomGridStackWidget } from '../../../services/grid-widget-config.service';
import { CustomWidgetComponent } from '../custom-widget/custom-widget.component';
import { DialogModule } from 'primeng/dialog';
import { HeaderComponent } from '../../atoms/header/header.component';
import { Button } from 'primeng/button';
import domtoimage from 'dom-to-image-more';
import { StyleClassModule } from 'primeng/styleclass';
import { GlobalsStore } from '../../../stores/globals.state';
import { NgClass } from '@angular/common';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { FormsModule } from '@angular/forms';
import { WegweiserStore } from '../../../stores/wegweiser-api/wegweiser.state';

@Component({
  selector: 'app-widget-export',
  standalone: true,
  imports: [
    CustomWidgetComponent,
    DialogModule,
    HeaderComponent,
    Button,
    StyleClassModule,
    NgClass,
    ToggleButtonModule,
    FormsModule,
  ],
  templateUrl: './widget-export.component.html',
  styleUrl: './widget-export.component.scss',
})
export class WidgetExportComponent implements OnInit {
  readonly widgetStore = inject(WidgetStore);
  readonly wegweiserStore = inject(WegweiserStore);
  readonly globalsStore = inject(GlobalsStore);
  @Input() widgetId!: string;
  @ViewChild('exportContainer', { static: false }) exportContainer!: ElementRef;

  widget: CustomGridStackWidget | null = null;
  exportId = 'export';
  portrait = true;
  halfPage = false;

  /**
   * Initializes the component after Angular has completed setting up the component's inputs.
   * This method retrieves a copy of the widget based on the provided widgetId and exportId
   * and assigns it to the local widget property.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.widget = this.widgetStore.getAsCopy(this.widgetId, this.exportId);
  }

  /**
   * Retrieves the caption of a widget based on the provided export ID.
   * The caption is fetched from the widget store.
   *
   * @return {string} The caption of the widget.
   */
  get widgetCaption(): string {
    return this.widget?.widget.context !== 'external'
      ? this.widgetStore.widgetCaptionById(this.exportId)
      : this.wegweiserStore.getWidgetCaption(this.widget?.widget);
    //return this.widgetStore.widgetCaptionById(this.exportId);
  }

  /**
   * Closes the export dialog by removing the export widget ID from the store
   * and resetting the widget to export to null.
   *
   * @return {void} Does not return any value.
   */
  closeExportDialog(): void {
    this.widgetStore.remove(this.exportId);
    this.widgetStore.setWidgetToExport(null);
  }

  /**
   * Displays the sidebar configuration for a specific widget.
   *
   * This method opens the configuration panel for the widget associated with the given export ID.
   *
   * @return {void} No return value.
   */
  showConfig(): void {
    // show sidebar config
    this.widgetStore.openConfig(this.exportId);
  }

  /**
   * Exports the widget container as a PNG image.
   *
   * This method captures the current rendering of the widget container as a PNG image and
   * initiates a download with a dynamically generated filename based on the widget's caption.
   * It ensures a high-quality image is created and avoids cached styles during the export.
   *
   * @return {void} This method does not return any value.
   */
  exportWidget(): void {
    if (!this.exportContainer) return;

    domtoimage
      .toPng(this.exportContainer.nativeElement, {
        bgcolor: 'white', // Prevent transparency issues
        quality: 1, // Ensure high-quality output
        cacheBust: true, // Avoid cached styles
      })
      .then((dataUrl: string) => {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = this.widgetStore.widgetCaptionById(this.exportId, true);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.closeExportDialog();
      })
      .catch((error: string) => {
        console.error('Error exporting widget:', error);
      });
  }
}
